/**
 * karty-widget — embeddable map widget for the karty gateway.
 *
 * Single self-contained bundle (MapLibre GL JS included) so a customer site
 * needs exactly one <script> tag and no build step, module loader, or
 * knowledge of MapLibre. See ../docs/embed.md for the customer-facing guide.
 *
 * Design notes (see TZ.md Фаза 4/Фаза 3б for the fuller reasoning):
 * - Base URL is inferred from the script's own src, so a copy-pasted embed
 *   snippet keeps working after we move to a real domain — no hardcoded host
 *   in customer code.
 * - The style.json fetch is done here (not left to MapLibre) specifically so
 *   a bad key/origin/quota shows a readable overlay instead of a blank grey
 *   box — the single biggest "is this thing broken" complaint any embeddable
 *   map widget gets from non-technical users.
 * - Search sends on Enter only, never per keystroke: Nominatim is not an
 *   autocomplete backend, and per-keystroke queries would burn through a
 *   customer's geocode quota and load on a 2-vCPU box for no benefit.
 * - Chrome (search box, route chip, error overlay) is styled from
 *   DESIGN.md's tokens, hardcoded as hex — the widget never loads a
 *   stylesheet or a custom font (system-ui only), it's a guest on the
 *   customer's page, not the other way around.
 */
import * as maplibregl from 'maplibre-gl';
import maplibreCss from 'maplibre-gl/dist/maplibre-gl.css';

// __KARTY_VERSION__ is injected by esbuild's `define` from package.json
// (see build.mjs) — package.json is the single source of the version
// number. It used to be hardcoded here as a separate constant, which meant
// bumping one and not the other either loaded the wrong worker file or
// silently rebuilt over the previous version's dist output.
const VERSION = __KARTY_VERSION__;
const WORKER_FILENAME = `karty-worker-${VERSION}.js`;
let workerUrlConfigured = false;
// Module-level, not per-instance: several KartyMap instances on one page
// (rare, but addMarker/attachSearch already support it) must not collide on
// source/layer ids if each calls setMarkers().
let markerSetCounter = 0;
// The marker's VALUE (not just its presence) is checked — see ensureCss.
// This lets 1.0 and a newly-flipped 1.1 coexist correctly on the same page
// during the few minutes a cached page might still reference the old
// unversioned alias while a freshly-loaded tab already got the new one.
const CSS_MARKER_ATTR = 'data-karty-css-version';
const DEFAULT_STYLE = 'warm';
const DEFAULT_CENTER = [37.6173, 55.7558]; // Moscow — a reasonable default when the embedder forgets to set one
const DEFAULT_ZOOM = 10;
const SEARCH_RESULT_LIMIT = 5;

// Styles whose basemap is visually dark — their chrome (search box, route
// chip, error overlay) switches to a light-on-dark palette automatically.
// Kept in sync with style-tools/gen-public.mjs's THEMES list by hand (no
// shared module between the widget and the style generator — different
// packages, different deploy targets); if a new dark theme is added there,
// add it here too.
const DARK_CHROME_STYLES = new Set(['dark', 'black']);

// setMarkers()'s cluster styling reuses the same accent used for the search
// marker/route line above (#006B71 light / #2DD4BF dark, see _chromeDark's
// other uses) — one brand color across the whole widget, not a separate
// palette invented for this one feature. Text color on the cluster-count
// bubble is the same light/dark-ink pairing DESIGN.md's cabinet tokens use
// for text ON that accent (--accent-text-on).
const MARKER_ACCENT = { light: '#006B71', dark: '#2DD4BF' };
const MARKER_ACCENT_TEXT_ON = { light: '#ffffff', dark: '#0F172A' };
// setHeatmap()'s default ramp: transparent -> brand accent -> warm high-end,
// the same shape MapLibre's own heatmap-layer example uses (low density
// fades to nothing instead of a hard edge). Kept independent of light/dark
// chrome — a heatmap sits ON the map's own colors, not the widget's chrome,
// so it doesn't need a dark-mode variant the way the marker accent does.
// Stays fully transparent up to density 0.12 (not the first version's 0.2,
// which had an opaque-ish dark teal at low density — that's the muddy smear
// low-density areas showed before this was tightened) — alpha lives entirely
// in this ramp, `heatmap-opacity` is intentionally left at its default 1
// rather than stacked on top, which would just double-fade everything.
const HEATMAP_DEFAULT_COLOR = [
  'interpolate', ['linear'], ['heatmap-density'],
  0, 'rgba(0, 107, 113, 0)',
  0.12, 'rgba(0, 107, 113, 0)',
  0.28, 'rgba(0, 107, 113, 0.55)',
  0.48, 'rgba(45, 190, 180, 0.75)',
  0.66, 'rgba(250, 204, 21, 0.85)',
  0.84, 'rgba(249, 115, 22, 0.9)',
  1, 'rgba(220, 38, 38, 0.95)',
];

// MapLibre/Mapbox GL JS zoom levels line up one below classic 256px
// slippy-map tile zoom (their reference tile is 512px), so metres-per-pixel
// at their zoom Z is 78271.517·cos(lat)/2^Z — half the commonly quoted
// 156543.03392 constant, which is for 256px tiles. Verified against the
// widget's own tile zoom ranges (style JSON minzoom/maxzoom) lining up with
// this, not assumed from memory alone.
const METERS_PER_PIXEL_AT_EQUATOR_Z0 = 78271.517;

/** A MapLibre `heatmap-radius` expression holding a constant real-world
 * radius (in metres) as the map is zoomed, instead of a constant pixel
 * count — see setHeatmap()'s doc comment for why. Growth is capped past a
 * "knee" zoom so a single isolated point doesn't balloon into a
 * full-screen blur at max zoom; beyond the knee the radius is flat. */
function heatmapRadiusExpression(radiusMeters, lat) {
  const metersPerPixelAtZ0 = METERS_PER_PIXEL_AT_EQUATOR_Z0 * Math.cos((lat * Math.PI) / 180);
  const maxPx = 180;
  const pxAtZoom = (z) => (radiusMeters * Math.pow(2, z)) / metersPerPixelAtZ0;
  const kneeZoom = Math.log2((maxPx * metersPerPixelAtZ0) / radiusMeters);
  const stops = [0, pxAtZoom(0)];
  for (const z of [6, 10, 13]) {
    if (z < kneeZoom) stops.push(z, pxAtZoom(z));
  }
  stops.push(Math.max(kneeZoom, 1), maxPx, Math.max(kneeZoom, 1) + 4, maxPx);
  return ['interpolate', ['exponential', 2], ['zoom'], ...stops];
}

function meanLatitude(items) {
  if (!items.length) return 0;
  return items.reduce((sum, item) => sum + item.lngLat[1], 0) / items.length;
}

// Rough metres-per-degree at a given latitude — good enough for a grid-hash
// density estimate, not for anything precision-sensitive.
function metersPerDegree(lat) {
  return { lat: 111320, lon: 111320 * Math.cos((lat * Math.PI) / 180) };
}

/** Estimates a `heatmap-intensity` from the data itself instead of always
 * defaulting to 1, which saturates solid red with real, naturally-clustered
 * data (confirmed with ~7000 real Barnaul POIs) or looks pale with sparse
 * data — see setHeatmap()'s doc comment. Grid-hashes points into cells sized
 * ~radiusMeters/2, sums each point's own cell plus its 8 neighbors as a
 * cheap proxy for "local weighted density within about radiusMeters", then
 * calibrates intensity so the 95th percentile of that density (not the max,
 * so one outlier cluster doesn't wash out everything else) lands near the
 * top of the color ramp. TARGET_DENSITY is empirical, tuned against that
 * same real dataset — MapLibre's heatmap density function isn't a documented
 * closed form, so this is a calibrated heuristic, not an exact inverse. */
function estimateHeatmapIntensity(items, radiusMeters) {
  if (items.length < 2) return 1;
  const lat = meanLatitude(items);
  const perDeg = metersPerDegree(lat);
  const cellSizeLon = (radiusMeters / 2) / perDeg.lon;
  const cellSizeLat = (radiusMeters / 2) / perDeg.lat;
  const cellOf = (item) => `${Math.floor(item.lngLat[0] / cellSizeLon)}:${Math.floor(item.lngLat[1] / cellSizeLat)}`;
  const cells = new Map();
  items.forEach((item) => {
    const key = cellOf(item);
    const w = item.weight != null ? item.weight : 1;
    cells.set(key, (cells.get(key) || 0) + w);
  });
  const neighborSum = (item) => {
    const cx = Math.floor(item.lngLat[0] / cellSizeLon);
    const cy = Math.floor(item.lngLat[1] / cellSizeLat);
    let sum = 0;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        sum += cells.get(`${cx + dx}:${cy + dy}`) || 0;
      }
    }
    return sum;
  };
  const densities = items.map(neighborSum).sort((a, b) => a - b);
  const p95 = densities[Math.floor(densities.length * 0.95)];
  const TARGET_DENSITY = 6;
  if (!p95) return 1;
  // Floor is low (not e.g. 0.15) on purpose: checked against ~7000 real
  // Barnaul POIs, whose p95 local density came out to 137, wanting
  // intensity ~0.04 — a denser real dataset than assumed when this was
  // first written, and a higher floor clamped it back into oversaturation.
  return Math.min(Math.max(TARGET_DENSITY / p95, 0.02), 8);
}
// Confirmed present on every style this widget serves (all styles share one
// glyph directory, see style-tools) — checked directly against a live
// style.json before picking this, not assumed.
const CLUSTER_TEXT_FONT = ['Noto Sans Medium'];

const ERROR_MESSAGES = {
  401: 'Карта недоступна: неверный или отозванный ключ доступа.',
  402: 'Карта недоступна: исчерпан месячный лимит тарифа.',
  403: 'Карта недоступна: этот домен не разрешён для данного ключа.',
  429: 'Карта временно недоступна: превышен лимит запросов. Попробуйте обновить страницу через минуту.',
};
const GENERIC_ERROR_MESSAGE = 'Не удалось загрузить карту.';

// --- base URL inference ------------------------------------------------

let overriddenBaseUrl = null;

function inferBaseUrl() {
  if (overriddenBaseUrl) return overriddenBaseUrl;
  const cur = document.currentScript;
  if (cur && cur.src) {
    try {
      return new URL(cur.src).origin;
    } catch (e) {
      /* fall through */
    }
  }
  // Fallback for cases where currentScript is unavailable (e.g. the script
  // was inserted dynamically after the fact) — find our own <script> tag by
  // its known filename pattern rather than guessing.
  const scripts = document.getElementsByTagName('script');
  for (let i = scripts.length - 1; i >= 0; i--) {
    const src = scripts[i].src || '';
    if (/karty(-[\d.]+)?\.js/.test(src)) {
      try {
        return new URL(src).origin;
      } catch (e) {
        /* keep looking */
      }
    }
  }
  return ''; // caller must then rely on Karty.setBaseUrl()
}

// --- one-time CSS injection ---------------------------------------------

function ensureCss() {
  const existing = document.querySelector(`style[${CSS_MARKER_ATTR}]`);
  if (existing) {
    if (existing.getAttribute(CSS_MARKER_ATTR) === VERSION) return;
    // A different widget version's stylesheet is present (mid-rollout page,
    // or a customer embedding two versions by mistake) — replace it rather
    // than stacking both, so overridden rules don't fight each other.
    existing.remove();
  }
  const style = document.createElement('style');
  style.setAttribute(CSS_MARKER_ATTR, VERSION);
  style.textContent = maplibreCss + '\n' + CHROME_CSS;
  document.head.appendChild(style);
}

/**
 * MapLibre parses tiles in a Web Worker. Its own worker entry point is an ES
 * module that imports a second file (maplibre-gl-shared.mjs) — that doesn't
 * exist standalone once we've bundled everything into one IIFE, so MapLibre
 * fails with "Worker failed to load" unless we point it at a worker bundle we
 * built separately (see build.mjs) and host next to the main widget file.
 * Must be called once, before the first `new maplibregl.Map(...)`.
 */
function ensureWorkerUrl(baseUrl) {
  if (workerUrlConfigured) return;
  workerUrlConfigured = true;
  maplibregl.setWorkerUrl(`${baseUrl}/widget/${WORKER_FILENAME}`);
}

// Every rule here is prefixed with karty-widget- specifically so it cannot
// collide with a customer page's existing class names — we're a guest on
// their site, not the other way around. Colors are DESIGN.md's tokens,
// hardcoded as hex (the widget never fetches a stylesheet or a design-token
// file — one more network request for no benefit, and a customer's CSP
// might not even allow it). `.karty-widget-chrome-dark` on the container
// (set from the style name, see DARK_CHROME_STYLES) switches every rule
// below to the dark-theme token values in one pass.
const CHROME_CSS = `
.karty-widget-container { position: relative; font-family: system-ui, sans-serif; }

/* --- error overlay (DESIGN.md 8.2) --- */
.karty-widget-error-overlay {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.92); font-size: 14px; line-height: 1.4; color: #334155; text-align: center;
  padding: 16px; box-sizing: border-box; z-index: 10;
}
.karty-widget-error-overlay__box { max-width: 320px; display: flex; flex-direction: column; align-items: center; gap: 8px; }
.karty-widget-chrome-dark .karty-widget-error-overlay { background: rgba(11,18,32,0.92); color: #F1F5F9; }

/* --- search (DESIGN.md 8.2/8.3) --- */
.karty-widget-search-wrap {
  position: absolute; top: 10px; left: 10px; z-index: 5;
  width: min(280px, calc(100% - 20px)); font-size: 14px;
}
.karty-widget-search-box {
  display: flex; align-items: center; gap: 8px;
  background: #fff; border: 1px solid #CBD5E1; border-radius: 6px;
  padding: 8px 10px; box-shadow: 0 1px 2px rgba(15,23,42,0.12);
}
.karty-widget-chrome-dark .karty-widget-search-box {
  background: rgba(18,26,43,0.95); border-color: #263349;
}
.karty-widget-search-box svg { flex-shrink: 0; color: #94A3B8; }
.karty-widget-chrome-dark .karty-widget-search-box svg { color: #64748B; }
.karty-widget-search {
  flex-grow: 1; min-width: 0; border: none; outline: none; background: transparent;
  font: inherit; color: #334155;
}
.karty-widget-search::placeholder { color: #94A3B8; }
.karty-widget-chrome-dark .karty-widget-search { color: #F1F5F9; }
.karty-widget-chrome-dark .karty-widget-search::placeholder { color: #64748B; }
.karty-widget-search-clear {
  flex-shrink: 0; border: none; background: none; padding: 2px; margin: 0; cursor: pointer;
  color: #94A3B8; display: flex;
}
.karty-widget-search-clear:hover { color: #334155; }
.karty-widget-chrome-dark .karty-widget-search-clear:hover { color: #F1F5F9; }
.karty-widget-search-spinner {
  flex-shrink: 0; width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid #E2E8F0; border-top-color: #006B71; animation: karty-widget-spin 0.7s linear infinite;
}
.karty-widget-chrome-dark .karty-widget-search-spinner { border-color: #263349; border-top-color: #2DD4BF; }
@keyframes karty-widget-spin { to { transform: rotate(360deg); } }

.karty-widget-search-results {
  list-style: none; margin: 4px 0 0; padding: 4px 0;
  background: #fff; border-radius: 8px; box-shadow: 0 1px 2px rgba(15,23,42,0.12);
  max-height: 220px; overflow-y: auto;
}
.karty-widget-chrome-dark .karty-widget-search-results { background: #121A2B; }
.karty-widget-search-result {
  padding: 8px 12px; font-size: 13px; color: #334155; cursor: pointer;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.karty-widget-chrome-dark .karty-widget-search-result { color: #F1F5F9; }
.karty-widget-search-result--active { background: #F1F5F9; }
.karty-widget-chrome-dark .karty-widget-search-result--active { background: #1A2438; }
.karty-widget-search-status {
  padding: 8px 12px; font-size: 13px; color: #64748B;
}
.karty-widget-chrome-dark .karty-widget-search-status { color: #94A3B8; }

/* --- route chip (DESIGN.md 8.2) --- */
.karty-widget-route-chip {
  position: absolute; bottom: 12px; left: 12px; z-index: 5;
  display: flex; align-items: center; gap: 6px;
  background: #fff; border-radius: 6px; box-shadow: 0 1px 2px rgba(15,23,42,0.12);
  padding: 7px 11px; font-size: 13px; color: #334155;
}
.karty-widget-chrome-dark .karty-widget-route-chip { background: rgba(18,26,43,0.95); color: #F1F5F9; }
.karty-widget-route-chip svg { flex-shrink: 0; color: #006B71; }
.karty-widget-chrome-dark .karty-widget-route-chip svg { color: #2DD4BF; }
.karty-widget-route-chip--error svg { color: #DC2626; }
.karty-widget-chrome-dark .karty-widget-route-chip--error svg { color: #F87171; }
.karty-widget-route-chip__secondary { color: #64748B; }
.karty-widget-chrome-dark .karty-widget-route-chip__secondary { color: #94A3B8; }

/* --- popups: MapLibre's own classes, restyled per DESIGN.md 8.2 — this
   bundle owns its own MapLibre instance, these rules don't reach any other
   map a customer might separately embed elsewhere on the same page. --- */
.maplibregl-popup-content {
  border-radius: 10px; box-shadow: 0 1px 2px rgba(15,23,42,0.12);
  font-family: system-ui, sans-serif; font-size: 13px; padding: 10px 12px;
}
`;

// --- error overlay ---------------------------------------------------------

function showErrorOverlay(container, status) {
  if (container.querySelector('.karty-widget-error-overlay')) return; // show once, not per failed tile
  const message = ERROR_MESSAGES[status] || GENERIC_ERROR_MESSAGE;
  const overlay = document.createElement('div');
  overlay.className = 'karty-widget-error-overlay';
  const box = document.createElement('div');
  box.className = 'karty-widget-error-overlay__box';
  box.appendChild(warningIcon());
  const text = document.createElement('div');
  text.textContent = message; // textContent, not innerHTML — message is our own static string, but no reason to risk it
  box.appendChild(text);
  overlay.appendChild(box);
  container.appendChild(overlay);
}

// --- small inline icon set (Lucide-style: 24x24, 1.75 stroke, no fill) -----
// Kept as tiny functions building real SVG elements (not innerHTML strings)
// so nothing here ever runs untrusted text through the DOM as markup.

function svgEl(paths, viewBox) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('viewBox', viewBox || '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.75');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  for (const d of paths) {
    const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    p.setAttribute('d', d);
    svg.appendChild(p);
  }
  return svg;
}

function searchIcon() {
  const svg = svgEl(['M11 18a7 7 0 100-14 7 7 0 000 14z']);
  const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  line.setAttribute('x1', '21');
  line.setAttribute('y1', '21');
  line.setAttribute('x2', '16.65');
  line.setAttribute('y2', '16.65');
  svg.appendChild(line);
  return svg;
}

function clearIcon() {
  const svg = svgEl([]);
  svg.setAttribute('width', '14');
  svg.setAttribute('height', '14');
  for (const [x1, y1, x2, y2] of [
    [18, 6, 6, 18],
    [6, 6, 18, 18],
  ]) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', x1);
    line.setAttribute('y1', y1);
    line.setAttribute('x2', x2);
    line.setAttribute('y2', y2);
    svg.appendChild(line);
  }
  return svg;
}

function routeIcon() {
  const svg = svgEl(['M9 19h8.5a3.5 3.5 0 000-7h-11a3.5 3.5 0 010-7H15']);
  for (const [cx, cy] of [
    [6, 19],
    [18, 5],
  ]) {
    const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    c.setAttribute('cx', cx);
    c.setAttribute('cy', cy);
    c.setAttribute('r', '3');
    svg.appendChild(c);
  }
  return svg;
}

function warningIcon() {
  const svg = svgEl(['M12 3L2 20h20L12 3z', 'M12 10v4.5']);
  svg.setAttribute('width', '22');
  svg.setAttribute('height', '22');
  svg.style.color = '#DC2626';
  const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  dot.setAttribute('cx', '12');
  dot.setAttribute('cy', '17.5');
  dot.setAttribute('r', '0.5');
  dot.setAttribute('fill', 'currentColor');
  svg.appendChild(dot);
  return svg;
}

// --- formatting helpers (route chip) ---------------------------------------

function formatDistance(meters) {
  if (meters < 1000) return `${Math.round(meters)} м`;
  return `${(meters / 1000).toFixed(1).replace('.', ',')} км`;
}

function formatDuration(seconds) {
  const totalMin = Math.round(seconds / 60);
  if (totalMin < 60) return `${totalMin} мин`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
}

// --- the widget --------------------------------------------------------

let searchInstanceCounter = 0;

class KartyMap {
  /**
   * @param {HTMLElement} container
   * @param {object} opts
   * @param {string} opts.key - publishable API key (pk_live_...)
   * @param {string} [opts.style='warm'] - one of warm|light|dark|white|grayscale|black
   * @param {[number,number]} [opts.center]
   * @param {number} [opts.zoom]
   * @param {string} [opts.baseUrl] - override the inferred gateway base URL
   * @param {boolean} [opts.navigationControl=true]
   */
  constructor(container, opts) {
    if (!opts || !opts.key) {
      throw new Error('karty-widget: opts.key is required (a publishable API key, pk_live_...)');
    }
    ensureCss();
    container.classList.add('karty-widget-container');
    this._chromeDark = DARK_CHROME_STYLES.has(opts.style);
    if (this._chromeDark) container.classList.add('karty-widget-chrome-dark');

    this._container = container;
    this._key = opts.key;
    this._baseUrl = opts.baseUrl || inferBaseUrl();
    ensureWorkerUrl(this._baseUrl);
    this._map = null;
    this._errorShown = false;
    // Queued rather than a single callback: multiple onReady()/addMarker()
    // calls made before the map finishes loading must ALL run, not just the
    // last one registered (a real 1.0 bug — see TZ.md Фаза 3б М6).
    this._onReadyQueue = [];
    this._mapLoaded = false;
    this._routeChip = null;
    this._searchResultMarker = null;
    // Handles from setMarkers()/setHeatmap(), so remove() can null out their
    // internal `removed` flags — the map itself tears down their
    // sources/layers for free, but a marker-set handle's own async work (an
    // in-flight getClusterExpansionZoom) must not act on a dead map afterward.
    this._markerSets = [];
    // remove() can run while the style fetch above is still in flight (React
    // StrictMode mounts, unmounts, remounts a component synchronously in
    // dev) — without this flag the .then() below would go ahead and build a
    // second Map into a container whose owner already considers it gone,
    // leaking a WebGL context and leaving two search boxes in the DOM.
    this._removed = false;

    const styleName = opts.style || DEFAULT_STYLE;
    const styleUrl = `${this._baseUrl}/v1/styles/${styleName}.json?key=${encodeURIComponent(this._key)}`;

    fetch(styleUrl)
      .then((res) => {
        if (!res.ok) {
          showErrorOverlay(container, res.status);
          this._errorShown = true;
          throw new Error(`karty-widget: style fetch failed with HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((styleJson) => {
        if (this._errorShown || this._removed) return;
        this._map = new maplibregl.Map({
          container,
          style: styleJson,
          center: opts.center || DEFAULT_CENTER,
          zoom: opts.zoom != null ? opts.zoom : DEFAULT_ZOOM,
          attributionControl: true, // ODbL requires this; not an option a customer can turn off, see TZ.md Фаза 4
        });
        if (opts.navigationControl !== false) {
          this._map.addControl(new maplibregl.NavigationControl());
        }
        this._map.on('error', (e) => {
          const status = e && e.error && e.error.status;
          if (status && ERROR_MESSAGES[status]) {
            showErrorOverlay(container, status);
            this._errorShown = true;
          } else {
            // Non-HTTP errors (style parsing, WebGL context loss, etc.) are
            // logged, not overlaid — they're not the "bad key" story this
            // overlay exists for, and papering over them would hide real bugs.
            console.error('karty-widget: map error', e && e.error);
          }
        });
        // 'load', not immediately after construction — a 1.0 bug: firing
        // onReady() right after `new Map()` handed callers a map whose style
        // wasn't done loading yet, so addSource/addLayer (route()) threw
        // "Style is not done loading". 'load' is MapLibre's own signal that
        // the style AND its sources are ready.
        this._map.on('load', () => {
          if (this._removed) return;
          this._mapLoaded = true;
          const queue = this._onReadyQueue;
          this._onReadyQueue = [];
          queue.forEach((cb) => cb(this._map));
        });
      })
      .catch((err) => {
        if (!this._errorShown) {
          console.error('karty-widget:', err);
          showErrorOverlay(container, 0);
        }
      });
  }

  /** Raw MapLibre GL JS Map instance, once loaded (null until then). */
  get map() {
    return this._map;
  }

  /** Registers a callback fired once the map has finished loading (style +
   * sources ready — MapLibre's 'load' event). Every call is queued and run,
   * in order; calling this after the map has already loaded runs the
   * callback synchronously. */
  onReady(cb) {
    if (this._mapLoaded) cb(this._map);
    else this._onReadyQueue.push(cb);
    return this;
  }

  /**
   * Adds a marker, optionally with a popup. This is the customer's own
   * content on their own page — no sanitization is performed here, same as
   * MapLibre's own Popup.setHTML() contract. If you show data from a source
   * you don't control (user-generated content on your site), sanitize it
   * yourself before calling this.
   */
  addMarker(lngLat, opts) {
    opts = opts || {};
    const marker = new maplibregl.Marker({ color: opts.color });
    marker.setLngLat(lngLat);
    if (opts.popupHtml) {
      marker.setPopup(new maplibregl.Popup({ offset: 24 }).setHTML(opts.popupHtml));
    } else if (opts.popupText) {
      marker.setPopup(new maplibregl.Popup({ offset: 24 }).setText(opts.popupText));
    }
    if (this._map) marker.addTo(this._map);
    else this.onReady(() => marker.addTo(this._map));
    return marker;
  }

  /**
   * Renders many points at once as clustered circles — for tens/hundreds of
   * points, `addMarker()` in a loop degrades badly (each is a real DOM
   * element; a browser starts to struggle well before a few hundred). This
   * uses MapLibre's own native `cluster: true` GeoJSON source option, which
   * already bundles supercluster internally — no separate clustering
   * library needed (ФИШКИ.md §3.3 named the standalone `supercluster`
   * package, but the widget already ships MapLibre GL JS, which does this
   * out of the box; adding the standalone library on top would just be
   * dead weight).
   *
   * @param {Array<{lngLat: [number, number], [key: string]: any}>} items -
   *   each item's own extra fields (not nested under `properties`) are
   *   passed back verbatim to popupHtml/popupText/onClick — unlike a GeoJSON
   *   source's own `properties`, which MapLibre flattens and stringifies,
   *   these are the exact original objects, kept in a JS-side id→item map.
   * @param {object} [opts]
   * @param {boolean} [opts.cluster=true]
   * @param {string} [opts.color] - defaults to the widget's brand accent
   * @param {(item: object) => string} [opts.popupHtml]
   * @param {(item: object) => string} [opts.popupText]
   * @param {(item: object) => void} [opts.onClick]
   * @returns {{update(items: Array): void, remove(): void}}
   */
  setMarkers(items, opts) {
    opts = opts || {};
    const cluster = opts.cluster !== false;
    const color = opts.color || (this._chromeDark ? MARKER_ACCENT.dark : MARKER_ACCENT.light);
    const textOn = this._chromeDark ? MARKER_ACCENT_TEXT_ON.dark : MARKER_ACCENT_TEXT_ON.light;
    const sourceId = `karty-widget-markers-${markerSetCounter++}`;
    const clusterLayerId = `${sourceId}-clusters`;
    const countLayerId = `${sourceId}-count`;
    const pointLayerId = `${sourceId}-points`;

    // Feature `id` (a plain incrementing index) is the join key back to the
    // original item — see the constructor param doc above for why this is
    // used instead of GeoJSON `properties`.
    let itemsById = new Map();
    const toGeoJSON = (list) => {
      itemsById = new Map(list.map((item, i) => [i, item]));
      return {
        type: 'FeatureCollection',
        features: list.map((item, i) => ({
          type: 'Feature',
          id: i,
          geometry: { type: 'Point', coordinates: item.lngLat },
          properties: {},
        })),
      };
    };

    let dead = false;
    const addLayers = (map) => {
      if (dead) return;
      map.addSource(sourceId, {
        type: 'geojson',
        data: toGeoJSON(items),
        cluster,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });
      if (cluster) {
        map.addLayer({
          id: clusterLayerId,
          type: 'circle',
          source: sourceId,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': color,
            'circle-opacity': 0.85,
            // Three size steps so a cluster of thousands doesn't look the
            // same as one of eleven — thresholds picked for typical
            // logistics/POI counts, not tuned against a specific dataset.
            'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 50, 26],
          },
        });
        map.addLayer({
          id: countLayerId,
          type: 'symbol',
          source: sourceId,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-font': CLUSTER_TEXT_FONT,
            'text-size': 13,
          },
          paint: { 'text-color': textOn },
        });
      }
      map.addLayer({
        id: pointLayerId,
        type: 'circle',
        source: sourceId,
        filter: cluster ? ['!', ['has', 'point_count']] : ['all'],
        paint: {
          'circle-color': color,
          'circle-radius': 7,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });

      if (cluster) {
        map.on('click', clusterLayerId, (e) => {
          // Read everything off the event SYNCHRONOUSLY, before the promise
          // below — MapLibre's click event/feature objects are only valid
          // for the duration of the handler's synchronous execution, and
          // reading them after an await intermittently threw inside
          // MapLibre's own internal code in testing (not this widget's
          // code — confirmed by calling the exact same steps directly
          // against the map instance, which worked every time; only real,
          // CDP-synthesized clicks followed by a later property read failed).
          const clusterId = e.features[0].properties.cluster_id;
          const coordinates = e.features[0].geometry.coordinates.slice();
          const source = map.getSource(sourceId);
          if (!source) return;
          // MapLibre 6's getClusterExpansionZoom returns a Promise (older
          // majors used a callback) — checked against the exact bundled
          // version before writing this, not assumed.
          source
            .getClusterExpansionZoom(clusterId)
            .then((zoom) => {
              if (dead) return;
              map.easeTo({ center: coordinates, zoom });
            })
            .catch(() => {
              /* an already-gone/invalid cluster id (e.g. data changed between click and resolve) — nothing to zoom to */
            });
        });
        map.on('mouseenter', clusterLayerId, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', clusterLayerId, () => { map.getCanvas().style.cursor = ''; });
      }
      map.on('click', pointLayerId, (e) => {
        const item = itemsById.get(e.features[0].id);
        if (!item) return;
        if (opts.popupHtml || opts.popupText) {
          const popup = new maplibregl.Popup({ offset: 12 });
          if (opts.popupHtml) popup.setHTML(opts.popupHtml(item));
          else popup.setText(opts.popupText(item));
          popup.setLngLat(e.features[0].geometry.coordinates.slice()).addTo(map);
        }
        if (opts.onClick) opts.onClick(item);
      });
      map.on('mouseenter', pointLayerId, () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', pointLayerId, () => { map.getCanvas().style.cursor = ''; });
    };

    if (this._map) addLayers(this._map);
    else this.onReady(addLayers);

    const handle = {
      update: (newItems) => {
        if (dead || !this._map) return;
        const source = this._map.getSource(sourceId);
        if (source) source.setData(toGeoJSON(newItems));
      },
      remove: () => {
        dead = true;
        this._markerSets = this._markerSets.filter((s) => s !== handle);
        if (!this._map) return;
        [clusterLayerId, countLayerId, pointLayerId].forEach((id) => {
          if (this._map.getLayer(id)) this._map.removeLayer(id);
        });
        if (this._map.getSource(sourceId)) this._map.removeSource(sourceId);
      },
      _markDead: () => { dead = true; },
    };
    this._markerSets.push(handle);
    return handle;
  }

  /**
   * Renders a density heatmap from weighted points — for showing WHERE
   * activity concentrates (order volume, foot traffic) rather than individual
   * markers, which stop being readable past a few hundred points. Pure
   * client-side rendering (MapLibre's built-in `heatmap` layer type), no
   * gateway call and no quota cost.
   *
   * Two mistakes in the first version of this method, found by comparing
   * against MapLibre's own reference example and real data (2026-09-30):
   * 1. A radius fixed in PIXELS looks fine at one zoom and wrong at every
   *    other one — a fixed screen-space radius covers less and less real
   *    ground as you zoom in, so the same points that blend into a smooth
   *    field at city view fall apart into isolated blobs up close. The fix
   *    is a radius fixed in METRES (`opts.radiusMeters`), converted to
   *    pixels per zoom level via the Web Mercator scale
   *    (metres/pixel = 78271.517·cos(lat) / 2^zoom — the 78271.517 constant,
   *    not the more commonly quoted 156543, is correct for MapLibre/Mapbox
   *    GL's 512px-tile zoom convention, confirmed against their own zoom
   *    levels lining up one below classic 256px slippy-map tile zoom).
   *    A crossfade to a separate circle layer at high zoom (the other
   *    standard fix, and what this method tried first) was dropped — it
   *    still needs the radius fix underneath or the "crisp dots" phase is
   *    reached at an arbitrary zoom instead of "once points are actually far
   *    apart in real distance", and `setMarkers()` already covers "crisp
   *    dots up close" via clustering.
   * 2. `heatmap-intensity` fixed at 1 saturates solid red with real,
   *    naturally-clustered data (confirmed with ~7000 real Barnaul OSM
   *    POIs — city center went solid red edge-to-edge at intensity 1) or
   *    looks pale with sparse data. Left unset, intensity is now derived
   *    from the data itself: a coarse grid hash estimates each point's
   *    local weighted density within roughly `radiusMeters`, and intensity
   *    is set so the 95th-percentile density (not the max, so one freak
   *    cluster doesn't wash out everything else) lands near the top of the
   *    color ramp. `opts.intensity` still overrides this when given.
   * @param {Array<{lngLat: [number, number], weight?: number}>} items
   * @param {object} [opts]
   * @param {number} [opts.radiusMeters=400] - real-world radius of influence per point
   * @param {number} [opts.intensity] - overrides the auto-calibrated density multiplier
   * @param {Array} [opts.color] - a MapLibre `heatmap-color` expression; defaults to the brand ramp
   * @returns {{update(items: Array): void, remove(): void}}
   */
  setHeatmap(items, opts) {
    opts = opts || {};
    const radiusMeters = opts.radiusMeters != null ? opts.radiusMeters : 400;
    const color = opts.color || HEATMAP_DEFAULT_COLOR;
    const sourceId = `karty-widget-heatmap-${markerSetCounter++}`;
    const heatmapLayerId = `${sourceId}-heat`;

    const toGeoJSON = (list) => ({
      type: 'FeatureCollection',
      features: list.map((item) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: item.lngLat },
        properties: { weight: item.weight != null ? item.weight : 1 },
      })),
    });

    let dead = false;
    const addLayer = (map) => {
      if (dead) return;
      map.addSource(sourceId, { type: 'geojson', data: toGeoJSON(items) });
      const radius = heatmapRadiusExpression(radiusMeters, meanLatitude(items));
      const intensity = opts.intensity != null ? opts.intensity : estimateHeatmapIntensity(items, radiusMeters);
      // Inserted before the first symbol layer so place/street labels stay
      // readable on top of the heatmap instead of getting buried under it —
      // the same reason a basemap draws labels last.
      const firstSymbolLayer = map.getStyle().layers.find((l) => l.type === 'symbol');
      map.addLayer(
        {
          id: heatmapLayerId,
          type: 'heatmap',
          source: sourceId,
          paint: {
            'heatmap-weight': ['get', 'weight'],
            'heatmap-intensity': intensity,
            'heatmap-color': color,
            'heatmap-radius': radius,
          },
        },
        firstSymbolLayer ? firstSymbolLayer.id : undefined,
      );
    };

    if (this._map) addLayer(this._map);
    else this.onReady(addLayer);

    const handle = {
      update: (newItems) => {
        if (dead || !this._map) return;
        const source = this._map.getSource(sourceId);
        if (source) source.setData(toGeoJSON(newItems));
      },
      remove: () => {
        dead = true;
        this._markerSets = this._markerSets.filter((s) => s !== handle);
        if (!this._map) return;
        if (this._map.getLayer(heatmapLayerId)) this._map.removeLayer(heatmapLayerId);
        if (this._map.getSource(sourceId)) this._map.removeSource(sourceId);
      },
      _markDead: () => { dead = true; },
    };
    this._markerSets.push(handle);
    return handle;
  }

  fitBounds(bounds, opts) {
    if (this._map) this._map.fitBounds(bounds, opts);
    else this.onReady((m) => m.fitBounds(bounds, opts));
    return this;
  }

  resize() {
    if (this._map) this._map.resize();
    return this;
  }

  /**
   * Tears the map down and releases its WebGL context. Needed for anything
   * that creates/destroys KartyMap instances repeatedly on the same page —
   * a theme switcher, an SPA route change, React StrictMode's double-mount
   * in dev — since browsers cap the number of live WebGL contexts per page
   * (commonly ~16) and a leaked one eventually breaks every map after it.
   *
   * Safe to call before the map has finished (or even started) loading: the
   * in-flight style fetch's .then() checks _removed and bails out instead of
   * building a Map into a container this instance no longer owns.
   */
  remove() {
    this._removed = true;
    if (this._map) {
      this._map.remove();
      this._map = null;
    }
    this._onReadyQueue = [];
    this._mapLoaded = false;
    this._markerSets.forEach((s) => s._markDead());
    this._markerSets = [];
    if (this._routeChip) {
      this._routeChip.remove();
      this._routeChip = null;
    }
    if (this._searchResultMarker) {
      this._searchResultMarker.remove();
      this._searchResultMarker = null;
    }
    if (this._searchCleanup) {
      this._searchCleanup();
      this._searchCleanup = null;
    }
    const overlay = this._container.querySelector('.karty-widget-error-overlay');
    if (overlay) overlay.remove();
    return this;
  }

  /**
   * Attaches a search box to the container that geocodes via the gateway on
   * Enter (never per keystroke — see module doc). Shows up to 5 results in
   * a dropdown (arrow keys + Enter to pick, or click); flies to the single
   * result directly if there's exactly one. Drops a pin at the picked
   * address by default (pass `marker: false` to skip it — e.g. if you'd
   * rather add your own via `onResult` + `addMarker`, with a custom popup).
   * `onResult(feature)` is called with the raw Nominatim result the visitor
   * picked, if provided.
   *
   * `autocomplete: true` additionally shows live suggestions as the visitor
   * types (debounced ~200ms), backed by a separate engine (Photon) built for
   * this, unlike Enter-only search above. Pilot feature (Altai region only
   * as of widget 1.2.0) — falls back to no live suggestions (Enter search
   * still works) if this deployment hasn't enabled it.
   *
   * Returns the <input> element itself (not the wrapper it's built inside),
   * with the `karty-widget-search` class still on it, same as v1.0 — kept
   * for anyone who queried it directly after calling this.
   */
  attachSearch(opts) {
    opts = opts || {};
    const instanceId = ++searchInstanceCounter;
    const listboxId = `karty-widget-search-listbox-${instanceId}`;

    const wrap = document.createElement('div');
    wrap.className = 'karty-widget-search-wrap';

    const box = document.createElement('div');
    box.className = 'karty-widget-search-box';
    box.appendChild(searchIcon());

    // The combobox role belongs on the input itself, not the wrapper — the
    // wrapper is just a positioning box, and screen readers expect
    // role/aria-expanded/aria-controls on the editable element per the ARIA
    // combobox pattern.
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = opts.placeholder || 'Поиск по адресу…';
    input.className = 'karty-widget-search';
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-controls', listboxId);
    box.appendChild(input);

    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'karty-widget-search-clear';
    clearBtn.setAttribute('aria-label', 'Очистить');
    clearBtn.hidden = true;
    clearBtn.appendChild(clearIcon());
    box.appendChild(clearBtn);

    const spinner = document.createElement('div');
    spinner.className = 'karty-widget-search-spinner';
    spinner.hidden = true;
    box.appendChild(spinner);

    wrap.appendChild(box);

    const list = document.createElement('ul');
    list.className = 'karty-widget-search-results';
    list.id = listboxId;
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    wrap.appendChild(list);

    let activeIndex = -1;
    let currentResults = [];

    const closeList = () => {
      list.hidden = true;
      list.innerHTML = '';
      activeIndex = -1;
      currentResults = [];
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    };

    const renderStatus = (text) => {
      list.innerHTML = '';
      const li = document.createElement('li');
      li.className = 'karty-widget-search-status';
      li.textContent = text;
      list.appendChild(li);
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const selectResult = (r) => {
      input.value = r.display_name || '';
      closeList();
      const lngLat = [parseFloat(r.lon), parseFloat(r.lat)];
      if (this._map) this._map.flyTo({ center: lngLat, zoom: 16 });
      if (opts.marker !== false) {
        if (this._searchResultMarker) {
          this._searchResultMarker.setLngLat(lngLat);
        } else {
          this._searchResultMarker = new maplibregl.Marker({ color: this._chromeDark ? '#2DD4BF' : '#006B71' }).setLngLat(lngLat);
          if (this._map) this._searchResultMarker.addTo(this._map);
          else this.onReady((m) => this._searchResultMarker && this._searchResultMarker.addTo(m));
        }
      }
      if (opts.onResult) opts.onResult(r);
    };

    const renderResults = (results) => {
      currentResults = results;
      activeIndex = -1;
      list.innerHTML = '';
      results.forEach((r, i) => {
        const li = document.createElement('li');
        li.className = 'karty-widget-search-result';
        li.id = `${listboxId}-opt-${i}`;
        li.setAttribute('role', 'option');
        li.textContent = r.display_name || ''; // textContent only — display_name comes from OSM, anyone can edit it
        li.addEventListener('mousedown', (e) => {
          e.preventDefault(); // keep focus on the input, don't fire a blur before the click registers
          selectResult(r);
        });
        list.appendChild(li);
      });
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const setActive = (index) => {
      const items = list.querySelectorAll('.karty-widget-search-result');
      items.forEach((el) => el.classList.remove('karty-widget-search-result--active'));
      if (index >= 0 && index < items.length) {
        items[index].classList.add('karty-widget-search-result--active');
        input.setAttribute('aria-activedescendant', items[index].id);
        items[index].scrollIntoView({ block: 'nearest' });
      } else {
        input.removeAttribute('aria-activedescendant');
      }
      activeIndex = index;
    };

    // Autocomplete (opt-in via attachSearch({autocomplete: true})) hits a
    // separate, lighter backend (Photon) built for per-keystroke use — unlike
    // the Enter-only default search above, which deliberately never fires
    // per keystroke (see module doc: Nominatim isn't built for that, and it'd
    // burn through a customer's geocode quota for no benefit). Photon's
    // response shape differs from Nominatim's, so results are normalized to
    // the same {display_name, lon, lat} shape renderResults/selectResult
    // already expect — no separate render path needed.
    let suggestDebounce = null;
    const runSuggest = (q) => {
      const url = `${this._baseUrl}/v1/geocode/suggest?key=${encodeURIComponent(this._key)}&q=${encodeURIComponent(q)}&limit=${SEARCH_RESULT_LIMIT}`;
      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`suggest failed with HTTP ${res.status}`);
          return res.json();
        })
        .then((geojson) => {
          const results = (geojson.features || []).map((f) => {
            const p = f.properties || {};
            return {
              display_name: [p.name, p.street, p.city, p.state].filter(Boolean).join(', '),
              lon: f.geometry.coordinates[0],
              lat: f.geometry.coordinates[1],
            };
          });
          if (!results.length) {
            renderStatus('Ничего не найдено');
            return;
          }
          renderResults(results);
        })
        .catch((err) => {
          console.error('karty-widget: suggest failed', err);
        });
    };

    const runSearch = (q) => {
      spinner.hidden = false;
      clearBtn.hidden = true;
      renderStatus('Поиск…');
      const url = `${this._baseUrl}/v1/geocode/search?key=${encodeURIComponent(this._key)}&q=${encodeURIComponent(q)}&limit=${SEARCH_RESULT_LIMIT}&accept-language=ru`;
      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`search failed with HTTP ${res.status}`);
          return res.json();
        })
        .then((results) => {
          spinner.hidden = true;
          clearBtn.hidden = false;
          if (!results || !results.length) {
            renderStatus('Ничего не найдено');
            return;
          }
          if (results.length === 1) {
            selectResult(results[0]);
            return;
          }
          renderResults(results);
        })
        .catch((err) => {
          console.error('karty-widget: search failed', err);
          spinner.hidden = true;
          clearBtn.hidden = false;
          renderStatus('Не удалось выполнить поиск');
        });
    };

    input.addEventListener('input', () => {
      clearBtn.hidden = input.value === '';
      // Any edit invalidates the previous result set — otherwise arrowing to
      // a result, editing the text, and pressing Enter would silently
      // re-select the stale highlighted item instead of searching the new text.
      closeList();
      if (opts.autocomplete) {
        if (suggestDebounce) clearTimeout(suggestDebounce);
        const q = input.value.trim();
        if (q.length < 2) return;
        suggestDebounce = setTimeout(() => runSuggest(q), 200);
      }
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (activeIndex >= 0 && currentResults[activeIndex]) {
          selectResult(currentResults[activeIndex]);
          return;
        }
        const q = input.value.trim();
        if (q) runSearch(q);
        return;
      }
      if (e.key === 'ArrowDown') {
        if (currentResults.length) {
          e.preventDefault();
          setActive(Math.min(activeIndex + 1, currentResults.length - 1));
        }
        return;
      }
      if (e.key === 'ArrowUp') {
        if (currentResults.length) {
          e.preventDefault();
          setActive(Math.max(activeIndex - 1, 0));
        }
        return;
      }
      if (e.key === 'Escape') {
        closeList();
      }
    });

    clearBtn.addEventListener('click', () => {
      input.value = '';
      clearBtn.hidden = true;
      closeList();
      input.focus();
      if (suggestDebounce) clearTimeout(suggestDebounce);
      if (this._searchResultMarker) {
        this._searchResultMarker.remove();
        this._searchResultMarker = null;
      }
    });

    const onDocumentClick = (e) => {
      if (!wrap.contains(e.target)) closeList();
    };
    document.addEventListener('click', onDocumentClick);

    this._container.appendChild(wrap);
    this._searchCleanup = () => {
      if (suggestDebounce) clearTimeout(suggestDebounce);
      document.removeEventListener('click', onDocumentClick);
      wrap.remove();
    };
    return input;
  }

  /**
   * Fetches a driving route between two [lon, lat] points via the gateway
   * (OSRM), draws it on the map, and shows a distance/time chip. Returns a
   * Promise resolving with the raw OSRM route object. Rejects (and shows an
   * error chip) if no route exists or a point falls outside the key's
   * allowed region.
   */
  route(from, to, opts) {
    opts = opts || {};
    const coords = `${from[0]},${from[1]};${to[0]},${to[1]}`;
    const url = `${this._baseUrl}/v1/route/driving/${coords}?overview=full&geometries=geojson&key=${encodeURIComponent(this._key)}`;
    return fetch(url)
      .then((res) => {
        if (!res.ok) {
          const message =
            res.status === 400
              ? 'Маршрут за пределами разрешённого региона'
              : ERROR_MESSAGES[res.status] || 'Не удалось построить маршрут';
          throw new Error(message);
        }
        return res.json();
      })
      .then((data) => {
        if (data.code !== 'Ok' || !data.routes || !data.routes.length) {
          throw new Error('Маршрут между этими точками не найден');
        }
        const routeData = data.routes[0];
        return new Promise((resolve) => {
          this.onReady((map) => {
            this._drawRoute(map, routeData.geometry);
            resolve();
          });
        }).then(() => {
          this._showRouteChip(formatDistance(routeData.distance), formatDuration(routeData.duration));
          if (opts.onResult) opts.onResult(routeData);
          return routeData;
        });
      })
      .catch((err) => {
        console.error('karty-widget: route failed', err);
        this._showRouteChip(null, null, err.message || 'Не удалось построить маршрут');
        throw err;
      });
  }

  /** Removes a route drawn by route(), if any, and hides its chip. */
  clearRoute() {
    this.onReady((map) => {
      if (map.getLayer('karty-widget-route-line')) map.removeLayer('karty-widget-route-line');
      if (map.getSource('karty-widget-route')) map.removeSource('karty-widget-route');
    });
    if (this._routeChip) {
      this._routeChip.remove();
      this._routeChip = null;
    }
    return this;
  }

  _drawRoute(map, geometry) {
    const sourceId = 'karty-widget-route';
    const layerId = 'karty-widget-route-line';
    const geojson = { type: 'Feature', geometry, properties: {} };
    const existing = map.getSource(sourceId);
    if (existing) {
      existing.setData(geojson);
    } else {
      map.addSource(sourceId, { type: 'geojson', data: geojson });
      map.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': this._chromeDark ? '#2DD4BF' : '#006B71',
          'line-width': 5,
          'line-opacity': 0.85,
        },
      });
    }
  }

  /**
   * Fetches an isochrone (the area reachable from `point` within each of
   * `opts.minutes`, e.g. [5, 10, 15]) and draws it as filled polygons on the
   * map. Returns a Promise resolving with the raw GeoJSON FeatureCollection.
   * Pilot feature (Altai region only as of widget 1.2.0, see ФИШКИ.md §1) —
   * a 503 here means this deployment hasn't enabled it yet, not a bug in
   * your call.
   */
  isochrone(point, opts) {
    opts = opts || {};
    const minutes = opts.minutes && opts.minutes.length ? opts.minutes : [15];
    const costing = opts.costing || 'auto';
    const url =
      `${this._baseUrl}/v1/isochrone?lon=${point[0]}&lat=${point[1]}` +
      `&contours=${minutes.join(',')}&costing=${encodeURIComponent(costing)}&key=${encodeURIComponent(this._key)}`;
    return fetch(url)
      .then((res) => {
        if (!res.ok) {
          throw new Error(ERROR_MESSAGES[res.status] || 'Не удалось построить зону доступности');
        }
        return res.json();
      })
      .then((geojson) => {
        return new Promise((resolve) => {
          this.onReady((map) => {
            this._drawIsochrone(map, geojson);
            resolve();
          });
        }).then(() => {
          if (opts.onResult) opts.onResult(geojson);
          return geojson;
        });
      })
      .catch((err) => {
        console.error('karty-widget: isochrone failed', err);
        throw err;
      });
  }

  /** Removes an isochrone drawn by isochrone(), if any. */
  clearIsochrone() {
    this.onReady((map) => {
      if (map.getLayer('karty-widget-isochrone-fill')) map.removeLayer('karty-widget-isochrone-fill');
      if (map.getLayer('karty-widget-isochrone-line')) map.removeLayer('karty-widget-isochrone-line');
      if (map.getSource('karty-widget-isochrone')) map.removeSource('karty-widget-isochrone');
    });
    return this;
  }

  _drawIsochrone(map, geojson) {
    const sourceId = 'karty-widget-isochrone';
    const existing = map.getSource(sourceId);
    if (existing) {
      existing.setData(geojson);
      return;
    }
    map.addSource(sourceId, { type: 'geojson', data: geojson });
    // fillColor/color come from the backend, one shade per contour ring —
    // reused as-is rather than re-deriving a palette here.
    map.addLayer({
      id: 'karty-widget-isochrone-fill',
      type: 'fill',
      source: sourceId,
      paint: { 'fill-color': ['get', 'fillColor'], 'fill-opacity': 0.25 },
    });
    map.addLayer({
      id: 'karty-widget-isochrone-line',
      type: 'line',
      source: sourceId,
      paint: { 'line-color': ['get', 'color'], 'line-width': 1.5 },
    });
  }

  _showRouteChip(distanceText, durationText, errorText) {
    if (!this._routeChip) {
      this._routeChip = document.createElement('div');
      this._routeChip.className = 'karty-widget-route-chip';
      this._container.appendChild(this._routeChip);
    }
    this._routeChip.innerHTML = '';
    this._routeChip.classList.toggle('karty-widget-route-chip--error', !!errorText);
    if (errorText) {
      this._routeChip.appendChild(warningSmallIcon());
      const span = document.createElement('span');
      span.textContent = errorText;
      this._routeChip.appendChild(span);
      return;
    }
    this._routeChip.appendChild(routeIcon());
    const distEl = document.createElement('span');
    distEl.textContent = distanceText;
    this._routeChip.appendChild(distEl);
    const sep = document.createElement('span');
    sep.className = 'karty-widget-route-chip__secondary';
    sep.textContent = `· ${durationText}`;
    this._routeChip.appendChild(sep);
  }
}

function warningSmallIcon() {
  const icon = warningIcon();
  icon.style.width = '16px';
  icon.style.height = '16px';
  icon.removeAttribute('style');
  icon.setAttribute('width', '16');
  icon.setAttribute('height', '16');
  return icon;
}

// --- auto-init from data-karty-* attributes -----------------------------

function readDataOpts(el) {
  const d = el.dataset;
  const opts = { key: d.kartyKey, style: d.kartyStyle };
  if (d.kartyLon != null && d.kartyLat != null) {
    opts.center = [parseFloat(d.kartyLon), parseFloat(d.kartyLat)];
  }
  if (d.kartyZoom != null) opts.zoom = parseFloat(d.kartyZoom);
  return opts;
}

function autoInit(root) {
  const els = (root || document).querySelectorAll('[data-karty-map]');
  els.forEach((el) => {
    if (el.__kartyInitialized) return;
    el.__kartyInitialized = true;
    if (!el.dataset.kartyKey) {
      console.error('karty-widget: element has data-karty-map but no data-karty-key, skipping', el);
      return;
    }
    const instance = new KartyMap(el, readDataOpts(el));
    el.__karty = instance;
    if (el.dataset.kartySearch != null) {
      instance.attachSearch({});
    }
  });
}

function runAutoInit() {
  autoInit(document);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', runAutoInit);
} else {
  runAutoInit();
}

// --- public API -----------------------------------------------------------

const Karty = {
  VERSION,
  /** Creates a map in `container` (an element or element id). */
  map(container, opts) {
    const el = typeof container === 'string' ? document.getElementById(container) : container;
    if (!el) throw new Error(`karty-widget: container not found: ${container}`);
    return new KartyMap(el, opts || {});
  },
  /** Overrides the inferred gateway base URL — for local testing, mainly. */
  setBaseUrl(url) {
    overriddenBaseUrl = url;
  },
  /** Re-scans the DOM for new [data-karty-map] elements (e.g. after an SPA route change). */
  autoInit,
};

if (typeof window !== 'undefined') {
  window.Karty = Karty;
}

export default Karty;
